import { useState, useEffect } from 'react';
import { Truck, User, RefreshCw, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { fetchEntregasClientes, reasignarConductor } from '@/services/entregasClientes.service';
import { fetchUsers } from '@/services/users.service';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const GestionEntregasEnCamino = ({ idLavanderia }) => {
  const { toast } = useToast();
  const [enCaminoEntregas, setEnCaminoEntregas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conductores, setConductores] = useState([]);
  const [isLoadingConductores, setIsLoadingConductores] = useState(false);
  const [selectedEntregas, setSelectedEntregas] = useState([]);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [newConductorId, setNewConductorId] = useState('');
  const [isReassigning, setIsReassigning] = useState(false);

  useEffect(() => {
    loadEnCamino();
    loadConductores();
  }, [idLavanderia]);

  const loadEnCamino = async () => {
    setIsLoading(true);
    try {
      const query = { estado: 'En Camino', tipo: 'Entrega' };
      if (idLavanderia) {
        query['lavanderia.id'] = idLavanderia;
      }
      const res = await fetchEntregasClientes({
        query,
        limit: 100,
        page: 1,
      });
      setEnCaminoEntregas(res.data || []);
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'No se pudieron cargar las entregas', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const loadConductores = async () => {
    setIsLoadingConductores(true);
    try {
      const res = await fetchUsers({ limit: 200, query: {} });
      const users = res.data || res.items || [];
      setConductores(users.filter((u) => (u.role || u.rol || '').toLowerCase() === 'conductor'));
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoadingConductores(false);
    }
  };

  const handleToggleEntrega = (id) => {
    setSelectedEntregas((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleReassignConductor = async () => {
    if (!newConductorId || selectedEntregas.length === 0) return;
    const conductor = conductores.find((c) => String(c._id || c.id) === String(newConductorId));
    if (!conductor) return;
    setIsReassigning(true);
    try {
      await reasignarConductor(selectedEntregas, {
        id: String(conductor._id || conductor.id),
        nombre: [conductor.nombre, conductor.apellido].filter(Boolean).join(' '),
      });
      toast({ title: 'Conductor reasignado', description: `${selectedEntregas.length} entrega(s) actualizada(s)` });
      setReassignDialogOpen(false);
      setSelectedEntregas([]);
      setNewConductorId('');
      loadEnCamino();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setIsReassigning(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border-orange-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-orange-600" />
              <CardTitle className="text-base">Entregas En Camino{!idLavanderia ? ' - Todas las Lavanderías' : ''}</CardTitle>
              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                {enCaminoEntregas.length}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={loadEnCamino} className="gap-1.5">
                <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              {selectedEntregas.length > 0 && (
                <Button
                  size="sm"
                  onClick={() => setReassignDialogOpen(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white gap-1.5"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Reasignar conductor ({selectedEntregas.length})
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-8 justify-center">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Cargando entregas...
            </div>
          ) : enCaminoEntregas.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-400">
              No hay entregas en camino
            </div>
          ) : (
            <div className="space-y-2">
              {enCaminoEntregas.map((entrega) => {
                const isSelected = selectedEntregas.includes(entrega._id);
                return (
                  <div
                    key={entrega._id}
                    onClick={() => handleToggleEntrega(entrega._id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200 hover:border-orange-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="h-4 w-4 accent-orange-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-gray-800">{entrega.consecutivo}</span>
                        <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                          En Camino
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span>{entrega.cliente?.nombre || 'Sin cliente'}</span>
                        <span>·</span>
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {entrega.conductor?.nombre || 'Sin conductor'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-medium text-gray-700">
                        {entrega.items?.reduce((s, i) => s + (i.cantidad_confirmada || i.cantidad_alistada || 0), 0) || 0} prendas
                      </div>
                      {entrega.fecha_envio && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {new Date(entrega.fecha_envio).toLocaleDateString('es-CO')}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reasignar Conductor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Reasignar {selectedEntregas.length} entrega(s) en camino a un nuevo conductor.
            </p>
            <div>
              <Label>Nuevo conductor</Label>
              <select
                value={newConductorId}
                onChange={(e) => setNewConductorId(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm mt-1"
                disabled={isLoadingConductores}
              >
                <option value="">Seleccionar conductor...</option>
                {conductores.map((c) => (
                  <option key={c._id || c.id} value={String(c._id || c.id)}>
                    {[c.nombre, c.apellido].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setReassignDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={handleReassignConductor}
                disabled={!newConductorId || isReassigning}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {isReassigning ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                    Reasignando...
                  </>
                ) : (
                  'Reasignar'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionEntregasEnCamino;
